import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardCard, { DashboardLink, TABLE_HEAD_CLASS } from './DashboardCard';

const STATUS_STYLES = {
  open: { label: 'Open', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  upcoming: { label: 'Upcoming', badge: 'bg-orange-50 text-orange-600 border-orange-200', dot: 'bg-orange-400' },
  yet_to_list: { label: 'Yet to List', badge: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-400' },
  listed: { label: 'Listed', badge: 'bg-teal-50 text-[#006666] border-teal-200', dot: 'bg-[#008080]' },
};

// Legend doubles as a status filter
const LEGEND = [
  { status: 'open', label: 'Issue open' },
  { status: 'upcoming', label: 'Upcoming' },
  { status: 'yet_to_list', label: 'Yet to list' },
  { status: 'listed', label: 'Listed' },
];

const MAX_ROWS = 12;

// ipos: utils/ipoDashboard.js toDashboardIpos
export const IpoCalendarCard = ({ board, ipos, reviewCounts = {}, loading, error, onRetry, query = '' }) => {
  const [statusFilter, setStatusFilter] = useState(null);
  const isMainboard = board.key === 'mainboard';

  const filtered = useMemo(() => (statusFilter ? ipos.filter((ipo) => ipo.status === statusFilter) : ipos), [ipos, statusFilter]);
  const rows = filtered.slice(0, MAX_ROWS);

  const legend = (
    <div role="group" aria-label="Filter by status" className="flex flex-wrap items-center gap-1 p-1 rounded-full border border-slate-200 bg-white">
      {LEGEND.map(({ status, label }) => {
        const active = statusFilter === status;
        return (
          <button
            key={status}
            type="button"
            aria-pressed={active}
            onClick={() => setStatusFilter(active ? null : status)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
              active ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${STATUS_STYLES[status].dot}`} />
            {label}
          </button>
        );
      })}
    </div>
  );

  // Under a search, an empty list means nothing matched, not that the market is quiet
  const searched = query.trim();
  let emptyMessage = searched
    ? `No ${board.label} with this status match “${searched}”.`
    : `No ${board.label} with this status right now.`;
  if (error && !ipos.length) {
    emptyMessage = (
      <>
        IPO data is temporarily unavailable.{' '}
        <button type="button" onClick={onRetry} className="font-bold text-[#008080] hover:underline">
          Retry
        </button>
      </>
    );
  } else if (!ipos.length) {
    emptyMessage = searched ? `No ${board.label} match “${searched}”.` : `No ${board.label} are open or scheduled right now.`;
  }

  return (
    <DashboardCard
      title={isMainboard ? 'Mainboard IPOs & FPOs 2026' : 'SME IPOs 2026'}
      subtitle={isMainboard ? 'Current issues trading, opening and listed at BSE and NSE' : 'Current issues on BSE SME and NSE Emerge'}
      aside={legend}
      footer={
        <DashboardLink to={isMainboard ? '/ipo-calendar' : '/sme-ipos'}>
          {isMainboard ? 'View the Full 2026 IPO Calendar' : `View All ${ipos.length} SME IPOs`}
        </DashboardLink>
      }
    >
      <div className="overflow-x-auto scrollbar-slim">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className={TABLE_HEAD_CLASS}>
              <th className="py-3 pl-4 sm:pl-5 pr-3 min-w-[220px]">Company Name</th>
              <th className="py-3 px-3 whitespace-nowrap">Bidding Dates</th>
              <th className="py-3 px-3 whitespace-nowrap">Offer Price</th>
              <th className="py-3 pl-3 pr-4 sm:pr-5 text-right">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-xs sm:text-[13px]">
            {loading &&
              Array.from({ length: 6 }).map((_, idx) => (
                <tr key={`skeleton-${idx}`} className="animate-pulse">
                  <td className="py-3 pl-4 sm:pl-5 pr-3">
                    <div className="h-4 w-44 rounded bg-slate-200" />
                    <div className="mt-1.5 h-3 w-24 rounded bg-slate-100" />
                  </td>
                  <td className="py-3 px-3"><div className="h-4 w-20 rounded bg-slate-100" /></td>
                  <td className="py-3 px-3"><div className="h-4 w-16 rounded bg-slate-100" /></td>
                  <td className="py-3 pl-3 pr-4 sm:pr-5"><div className="ml-auto h-5 w-16 rounded bg-slate-100" /></td>
                </tr>
              ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={4} className="py-10 text-center text-sm text-slate-500">
                  {emptyMessage}
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((ipo) => {
                const status = STATUS_STYLES[ipo.status];
                const reviews = reviewCounts[ipo.company];
                return (
                  <tr key={ipo.id} className="hover:bg-slate-50/70 transition-colors">
                    <td className="py-3 pl-4 sm:pl-5 pr-3">
                      <div className="flex items-center gap-2">
                        {ipo.link ? (
                          <Link to={ipo.link} className="font-semibold text-[#008080] hover:text-[#006666] hover:underline">
                            {ipo.company}
                          </Link>
                        ) : (
                          <span className="font-semibold text-slate-800">{ipo.company}</span>
                        )}
                        {reviews > 0 && (
                          <span
                            title={`${reviews} broker review${reviews === 1 ? '' : 's'}`}
                            className="px-1.5 py-px rounded-full bg-emerald-50 border border-emerald-200 text-[10px] font-bold text-emerald-700"
                          >
                            {reviews}
                          </span>
                        )}
                      </div>
                      {ipo.segment && <div className="text-[11px] text-slate-400 mt-0.5">{ipo.segment}</div>}
                    </td>
                    <td className={`py-3 px-3 whitespace-nowrap ${ipo.dates.startsWith('To be') ? 'text-slate-400' : 'text-slate-600'}`}>{ipo.dates}</td>
                    <td className={`py-3 px-3 whitespace-nowrap ${ipo.price.startsWith('₹') ? 'text-slate-800 font-medium' : 'text-slate-400'}`}>{ipo.price}</td>
                    <td className="py-3 pl-3 pr-4 sm:pr-5 text-right whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-[11px] font-bold ${status.badge}`}>
                        {ipo.status === 'open' && <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />}
                        {status.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      {!loading && filtered.length > MAX_ROWS && (
        <p className="px-4 sm:px-5 pb-3 text-[11px] text-slate-400">
          Showing {MAX_ROWS} of {filtered.length} issues.
        </p>
      )}
    </DashboardCard>
  );
};

export default IpoCalendarCard;
